// src/components/ProfileContactFields.js
// Presentational, reusable contact fields: phone, country, cascading state/province,
// city. Parent owns the state; this component is "dumb" and only renders + reports
// changes via onChange. Reused by: signup, the completion modal, the account page,
// the renewal gate.
//
// Props:
//   values   : { phoneNumber, country, stateProvince, city }
//   onChange : (partialUpdate) => void  — parent merges the partial into its state
//   errors   : optional per-field error strings { phoneNumber?, country?, ... }
import React from 'react';
import { COUNTRIES, SUBDIVISIONS, hasSubdivisions } from '../config/locationData';

function ProfileContactFields({ values = {}, onChange, errors = {} }) {
  const {
    phoneNumber = '',
    country = '',
    stateProvince = '',
    city = '',
  } = values;

  const subdivisions = hasSubdivisions(country) ? SUBDIVISIONS[country] : null;

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none ' +
    'focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white';

  const handleCountryChange = (e) => {
    // Reset state/province when the country changes — the old value belongs to a
    // different country's list and would otherwise be stale/invalid.
    onChange({ country: e.target.value, stateProvince: '' });
  };

  return (
    <div className="space-y-4">
      {/* Phone */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Phone Number
        </label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => onChange({ phoneNumber: e.target.value })}
          placeholder="e.g. +1 250 555 0123"
          className={inputClass}
        />
        {errors.phoneNumber && (
          <p className="text-red-600 text-sm mt-1">{errors.phoneNumber}</p>
        )}
      </div>

      {/* Country */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Country
        </label>
        <select value={country} onChange={handleCountryChange} className={inputClass}>
          <option value="">Select your country</option>
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {errors.country && (
          <p className="text-red-600 text-sm mt-1">{errors.country}</p>
        )}
      </div>

      {/* State / Province — dropdown for US/CA/AU, free text otherwise */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          State / Province
        </label>
        {subdivisions ? (
          <select
            value={stateProvince}
            onChange={(e) => onChange({ stateProvince: e.target.value })}
            className={inputClass}
          >
            <option value="">Select your state/province</option>
            {subdivisions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={stateProvince}
            onChange={(e) => onChange({ stateProvince: e.target.value })}
            placeholder="State / province / region (optional)"
            className={inputClass}
          />
        )}
        {errors.stateProvince && (
          <p className="text-red-600 text-sm mt-1">{errors.stateProvince}</p>
        )}
      </div>

      {/* City */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          City
        </label>
        <input
          type="text"
          value={city}
          onChange={(e) => onChange({ city: e.target.value })}
          placeholder="Enter your city"
          className={inputClass}
        />
        {errors.city && (
          <p className="text-red-600 text-sm mt-1">{errors.city}</p>
        )}
      </div>
    </div>
  );
}

export default ProfileContactFields;
